import React from 'react'
import { StyleProp, TouchableOpacity, View, ViewStyle } from 'react-native'
import { useTheme } from '@ui-kitten/components'
import { StyleService, useStyleSheet } from '@style/service'
import { Icon, Text } from '@components/base'
import {
  NoraHomeWalkthroughLayout,
  NoraHomeWalkthroughPresentation,
  NoraHomeWalkthroughStep,
  NoraHomeWalkthroughTargetId,
} from './types'

export const NoraHomeWalkthroughCard = ({
  step,
  stepIndex,
  stepCount,
  isFinalStep = false,
  layout,
  showArrow = false,
  arrowPlacement = 'top',
  arrowStyle,
  primaryActionLabel,
  onClose,
  onNext,
}: {
  step: NoraHomeWalkthroughStep
  stepIndex: number
  stepCount: number
  isFinalStep?: boolean
  layout: NoraHomeWalkthroughLayout
  showArrow?: boolean
  arrowPlacement?: 'top' | 'bottom'
  arrowStyle?: StyleProp<ViewStyle>
  primaryActionLabel?: string
  onClose: () => void
  onNext: () => void
}) => {
  const styles = useStyleSheet(themedStyles)
  const theme = useTheme()
  const isAnchoredStep = step.presentation === NoraHomeWalkthroughPresentation.Anchored
  const showSkip = !isAnchoredStep && !!step.showSkip
  const showAnchorArrow = showArrow || isAnchoredStep
  const isSettingsStep = isAnchoredStep && step.targetId === NoraHomeWalkthroughTargetId.Settings

  return (
    <View
      testID="nora-home-walkthrough-card-container"
      style={[
        styles.cardContainer,
        {
          maxWidth: layout.cardMaxWidth,
        },
      ]}
    >
      {showAnchorArrow && arrowPlacement === 'top' && (
        <View style={[styles.cardArrow, arrowStyle]} testID="nora-home-walkthrough-card-arrow" />
      )}
      <View
        style={[
          styles.card,
          {
            padding: layout.cardPadding,
            paddingTop: layout.cardTopPadding,
            gap: layout.cardGap,
          },
          isSettingsStep && { borderTopRightRadius: 16 },
        ]}
        testID="nora-home-walkthrough-card"
        accessibilityLabel={`Nora tutorial step ${stepIndex + 1} of ${stepCount}`}
      >
        <View
          style={[
            styles.cardIcon,
            {
              width: layout.cardIconSize,
              height: layout.cardIconSize,
              borderRadius: layout.cardIconSize / 2,
              backgroundColor: theme[step.iconBackgroundColor],
            },
          ]}
        >
          <Icon name={step.icon} size={layout.iconSize} color={theme[step.iconColor]} />
        </View>
        <View style={styles.copy}>
          <Text type="large" bold style={styles.title}>
            {step.title}
          </Text>
          <Text type="regular" style={[styles.body, { lineHeight: layout.bodyLineHeight }]}>
            {step.body}
          </Text>
        </View>
        <TouchableOpacity
          accessibilityLabel="Next Nora tutorial step"
          style={styles.nextButton}
          onPress={onNext}
        >
          <Text type="large" bold style={styles.nextText}>
            {primaryActionLabel ?? (isFinalStep ? 'Done' : 'Next')}
          </Text>
        </TouchableOpacity>
        {showSkip && (
          <TouchableOpacity
            accessibilityLabel="Skip Nora tutorial"
            style={styles.skipButton}
            onPress={onClose}
          >
            <Text type="regular" bold style={styles.skipText}>
              Skip for now
            </Text>
          </TouchableOpacity>
        )}
      </View>
      {showAnchorArrow && arrowPlacement === 'bottom' && (
        <View
          style={[styles.cardArrow, styles.cardArrowBottom, arrowStyle]}
          testID="nora-home-walkthrough-card-arrow"
        />
      )}
    </View>
  )
}

const themedStyles = StyleService.create({
  cardContainer: {
    width: '100%',
    alignItems: 'center',
  },
  cardArrow: {
    alignSelf: 'center',
    width: 0,
    height: 0,
    borderLeftWidth: 7,
    borderRightWidth: 7,
    borderBottomWidth: 7,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    borderBottomColor: 'theme.background',
    marginBottom: -1,
    zIndex: 1,
  },
  cardArrowBottom: {
    borderTopWidth: 7,
    borderBottomWidth: 0,
    borderTopColor: 'theme.background',
    borderBottomColor: 'transparent',
    marginTop: -1,
    marginBottom: 0,
  },
  card: {
    width: '100%',
    borderRadius: 24,
    backgroundColor: 'theme.background',
    alignItems: 'center',
  },
  cardIcon: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  copy: {
    gap: 10,
    alignItems: 'center',
  },
  title: {
    color: 'theme.text.primary',
    textAlign: 'center',
  },
  body: {
    color: 'theme.text.secondary',
    textAlign: 'center',
  },
  nextButton: {
    width: '100%',
    minHeight: 44,
    borderRadius: 22,
    backgroundColor: 'theme.primary.base',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
  },
  nextText: {
    color: 'theme.solid.white',
  },
  skipButton: {
    minHeight: 28,
    justifyContent: 'center',
  },
  skipText: {
    color: 'theme.text.primary',
  },
})
